/**
 * Profils locaux (plusieurs joueurs sur un même appareil — ex. deux enfants).
 *
 * RÈGLE PUBLIC ENFANTS (CLAUDE.md §6.1) : 100 % LOCAL. Un profil = un prénom/surnom
 * choisi par l'enfant + un avatar emoji, rien de plus. Aucune donnée n'est envoyée
 * ni partagée : pas de serveur, pas de compte en ligne, pas d'email, pas de mot de
 * passe. Le nom du profil ne transite JAMAIS en multijoueur (le snapshot réseau ne
 * contient que l'état du puzzle). Jouer en INVITÉ (sans profil) reste toujours possible.
 *
 * Mécanisme : chaque profil possède son propre espace de sauvegarde via un suffixe
 * de clé localStorage (`activeSuffix`). L'invité = suffixe vide = les clés
 * historiques (`oc_v9`, `oc_v10_score`…), donc la progression existante d'un joueur
 * sans profil est préservée telle quelle.
 */

export interface Profile {
  id: string;
  name: string;
  avatar: string;
  createdAt: number;
}

interface ProfileStore {
  profiles: Profile[];
  /** `null` = mode invité (aucun profil sélectionné). */
  activeId: string | null;
}

import { scheduleSync } from './account';

const KEY = 'oc_v11_profiles';

/** Clés de données propres à un profil (namespacées par le suffixe). */
const DATA_KEYS = ['oc_v9', 'oc_v9_seen', 'oc_v10_score'] as const;

/** Avatars proposés à la création (emoji uniquement — aucun asset, offline). */
export const AVATARS = [
  '🦊',
  '🐼',
  '🦄',
  '🐙',
  '🐳',
  '🦋',
  '🐝',
  '🦁',
  '🐸',
  '🐬',
  '🦖',
  '🐧',
  '🦉',
  '🐢',
  '🐰',
  '🌟',
  '🚀',
  '🍄',
  '🎈',
  '🎨',
] as const;

let store: ProfileStore = { profiles: [], activeId: null };

function persist(): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(store));
  } catch {
    /* stockage indisponible : mode dégradé silencieux */
  }
  scheduleSync();
}

/** Normalise un objet relu (JSON) en `ProfileStore` sûr. */
function sanitize(raw: unknown): ProfileStore {
  const o = (raw ?? {}) as Partial<ProfileStore>;
  const profiles = Array.isArray(o.profiles)
    ? o.profiles
        .filter((p): p is Profile => !!p && typeof p.id === 'string' && typeof p.name === 'string')
        .map((p) => ({
          id: p.id,
          name: String(p.name).slice(0, 14),
          avatar: typeof p.avatar === 'string' && p.avatar ? p.avatar : AVATARS[0],
          createdAt: typeof p.createdAt === 'number' ? p.createdAt : 0,
        }))
    : [];
  const activeId =
    typeof o.activeId === 'string' && profiles.some((p) => p.id === o.activeId) ? o.activeId : null;
  return { profiles, activeId };
}

/** Charge le registre des profils (à appeler AVANT loadProg/loadScore/loadSeen). */
export function loadProfiles(): void {
  let raw: unknown = {};
  try {
    raw = JSON.parse(localStorage.getItem(KEY) ?? '{}');
  } catch {
    raw = {};
  }
  store = sanitize(raw);
}

export function listProfiles(): readonly Profile[] {
  return store.profiles;
}

export function activeProfileId(): string | null {
  return store.activeId;
}

export function activeProfile(): Profile | null {
  return store.profiles.find((p) => p.id === store.activeId) ?? null;
}

/** Suffixe de clé localStorage du profil actif — `''` pour l'invité. */
export function activeSuffix(): string {
  return store.activeId ? '__' + store.activeId : '';
}

/** Identifiant court, non deviné (pas une donnée sensible : juste une clé de rangement). */
function genId(): string {
  const rnd = Math.floor(Math.random() * 46656)
    .toString(36)
    .padStart(3, '0');
  return 'p' + Date.now().toString(36) + rnd;
}

/**
 * Crée un profil, le rend actif et le persiste. Le nom est nettoyé (14 signes max,
 * repli « Joueur » si vide). Renvoie le profil créé.
 */
export function createProfile(name: string, avatar: string): Profile {
  const p: Profile = {
    id: genId(),
    name: name.trim().slice(0, 14) || 'Joueur',
    avatar: AVATARS.includes(avatar as (typeof AVATARS)[number]) ? avatar : AVATARS[0],
    createdAt: Date.now(),
  };
  store.profiles.push(p);
  store.activeId = p.id;
  persist();
  return p;
}

/** Bascule sur un profil (ou l'invité si `null`). L'appelant recharge ensuite les données. */
export function switchProfile(id: string | null): void {
  store.activeId = id && store.profiles.some((p) => p.id === id) ? id : null;
  persist();
}

/** Renomme un profil (14 signes max, repli « Joueur »). */
export function renameProfile(id: string, name: string): void {
  const p = store.profiles.find((q) => q.id === id);
  if (p) {
    p.name = name.trim().slice(0, 14) || 'Joueur';
    persist();
  }
}

/**
 * Supprime un profil ET toutes ses données locales (progression, score, « déjà vu »).
 * Si c'était le profil actif, on repasse en invité. L'appelant recharge les données.
 */
export function deleteProfile(id: string): void {
  const suffix = '__' + id;
  try {
    for (const base of DATA_KEYS) localStorage.removeItem(base + suffix);
  } catch {
    /* ignore */
  }
  store.profiles = store.profiles.filter((p) => p.id !== id);
  if (store.activeId === id) store.activeId = null;
  persist();
}

/** Total de points d'un profil (ou invité) — lecture directe, sans basculer. */
export function profilePts(id: string | null): number {
  try {
    const raw = localStorage.getItem('oc_v10_score' + (id ? '__' + id : ''));
    if (!raw) return 0;
    const s = JSON.parse(raw) as { total?: number };
    return typeof s.total === 'number' ? s.total : 0;
  } catch {
    return 0;
  }
}
